import { useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, GitMerge, X } from "lucide-react";
import {
  useGitPullRequest,
  useGitPullRequestFiles,
  useGitPullRequestCommits,
} from "@/hooks/git";
import type { GitPrFile } from "@/services/git/types";
import type { MergeMethod, PrDetailTab } from "./types";
import { PrPaneSkeleton, PaneLoader, PaneEmpty, PrStateBadge, MergeableBadge } from "./shared";
import { DiffPane, PrCommitsTab } from "./DiffPane";

// ─── PR Detail Pane ───────────────────────────────────────────────────────────

export function PrDetailPane({ repoFullName, number, onClose }: { repoFullName: string; number: number; onClose: () => void }) {
  const [innerTab, setInnerTab] = useState<PrDetailTab>("files");
  const [selectedFile, setSelectedFile] = useState<GitPrFile | null>(null);

  const { data: pr, isLoading: prLoading } = useGitPullRequest(repoFullName, number);
  const { data: files = [], isLoading: filesLoading } = useGitPullRequestFiles(
    repoFullName, innerTab === "files" ? number : null,
  );
  const { data: prCommits = [], isLoading: commitsLoading } = useGitPullRequestCommits(
    repoFullName, innerTab === "commits" ? number : null,
  );

  useEffect(() => { setSelectedFile(null); }, [number]);
  useEffect(() => { if (files[0]) setSelectedFile(files[0]); }, [files]);

  if (prLoading) return <PrPaneSkeleton />;
  if (!pr) return <PaneEmpty text="Pull request not found." />;


  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Meta header */}
      <div className="border-b border-border bg-muted/20 px-5 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <PrStateBadge state={pr.state} />
              <MergeableBadge state={pr.mergeable_state} />
              <p className="truncate text-sm font-semibold">{pr.title}</p>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-muted-foreground">
              <span className="text-primary/80">#{pr.number}</span>
              <span>{pr.author_login}</span>
              {pr.created_at && <span>{new Date(pr.created_at).toLocaleDateString()}</span>}
              <code className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{pr.head_branch}</code>
              <span>→</span>
              <code className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{pr.base_branch}</code>
              <span className="text-green-500">+{pr.additions}</span>
              <span className="text-red-400">−{pr.deletions}</span>
              <span>{pr.changed_files} file{pr.changed_files !== 1 ? "s" : ""}</span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <a href={pr.html_url} target="_blank" rel="noreferrer" className="studio-button text-xs">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button type="button" onClick={onClose} className="studio-button text-xs">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Conflict warning */}
        {pr.mergeable_state === "dirty" && pr.potentially_conflicting_files.length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-yellow-500/25 bg-yellow-500/8 p-2.5">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-yellow-500" />
            <div className="min-w-0">
              <p className="text-xs font-medium text-yellow-500">
                Conflicts in {pr.potentially_conflicting_files.length} file{pr.potentially_conflicting_files.length !== 1 ? "s" : ""}
              </p>
              <p className="mt-0.5 truncate font-mono text-[10px] text-yellow-500/70">
                {pr.potentially_conflicting_files.slice(0, 3).join(", ")}
                {pr.potentially_conflicting_files.length > 3 && ` +${pr.potentially_conflicting_files.length - 3} more`}
              </p>
            </div>
          </div>
        )}

        {/* Tabs + merge button row */}
        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex gap-0 rounded-lg border border-border bg-muted/20 p-0.5">
            {(["files", "commits"] as PrDetailTab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setInnerTab(t)}
                className={[
                  "rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors",
                  innerTab === t ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                ].join(" ")}
              >
                {t === "files" ? `Files (${pr.changed_files})` : "Commits"}
              </button>
            ))}
          </div>

          {/* Merge on GitHub */}
          {pr.state === "open" && (
            <a
              href={pr.html_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-primary/20"
            >
              <GitMerge className="h-3.5 w-3.5 text-primary" />
              Merge on GitHub
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </a>
          )}
        </div>
      </div>

      {/* Content */}
      {innerTab === "files" && (
        filesLoading
          ? <PaneLoader />
          : <DiffPane files={files} selectedFile={selectedFile} onSelectFile={setSelectedFile} />
      )}
      {innerTab === "commits" && (
        commitsLoading
          ? <PaneLoader />
          : <PrCommitsTab commits={prCommits} />
      )}
    </div>
  );
}
